// @flow
/* eslint-env browser */

import path from 'path';
import url from 'url';

import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import type { FormProps } from 'redux-form';

import { selectIsAuthenticatedUser, type User } from '@gandi/authentication';
import { Alert, Base, Button } from '@gandi/ui';
import { notifyDanger } from '@gandi/notifications';
import {
  fetchOrganizationsWithSubset,
  type Organization,
} from '@gandi/redux-sharing';
import { actions as formActions, SubmissionError } from 'gandi.react-form';
import { Intro } from 'gandi.react-public-layout';
import {
  escapeMarkdown,
  localeSelector,
  type TranslatorProps,
} from '@gandi/translate';

import type { WithRouterProps } from 'src/helpers/withRouter';
import type {
  Dispatch,
  EnhancedPublicUrls,
  GandiLogger,
  LayoutUrls,
  GetState,
} from 'src/types';

import { shouldRedirect, urlRedirectToHelpdesk } from 'src/helpers/redirect';
import { selectFromConfig } from '@gandi/redux-config/dist';
import Layout from '../../../containers/Layout';
import GandiVector from '../../../GandiVector';

import { saveQuestionId, setBackUrlParams } from '../../modules/layout';
import {
  fetchCurrentOrganization,
  fetchPersonalOrganization,
} from '../../modules/organization';

import { submitContactSupportForm } from '../modules';

import type { IValues, Subject } from '../types';

import styles from './ContactSupport.scss';
import ContactSupportForm from './ContactSupportForm';
import DocumentationGuides from './DocumentationGuides';
import AdditionalServices from './AdditionalServices';
import DocumentationSearch from './DocumentationSearch';
import FAQCollapsible from './FAQCollapsible';
import HelpHeader from './HelpHeader';
import PhishingAlert from './PhishingAlert';
import Chat from './Chat';

export type OwnProps = {
  assetPath: string => string,
  logger: GandiLogger,
  reduxFormProps: FormProps,
};

export type InjectedProps = {
  ...WithRouterProps<
    {
      category?: string,
      subject?: Subject,
      topic?: string,
    },
    {| +navigation?: ?string, +livechat?: ?string, +[string]: ?string |}
  >,
  translator: TranslatorProps,
};

export type StateProps = {
  backUrl?: string,
  chatEnabled: boolean,
  currentUrl: string,
  // eslint-disable-next-line react/no-unused-prop-types
  initialValues: IValues | Partial<IValues>,
  isAuthenticatedUser: boolean,
  isNotForFqdn: boolean,
  isForIp: boolean,
  isCorporate: boolean,
  locale: string,
  organizationsLoaded: boolean,
  personalDetails: Organization,
  phoneAdvisoryUrl: string,
  publicUrls: {| ...LayoutUrls, ...EnhancedPublicUrls |},
  qbotApi: string,
  qbotAvailableLanguages: Array<string>,
  qbotUrl: string,
  questionId: string,
  showPhishingWarn: boolean,
  showSupportWarn: boolean,
  sharingDetails: Organization,
  success: boolean,
  tmchUrl: string,
  updating: boolean,
  userCountryCode: string,
  userInfos: ?User,
};

export type Props = {| ...InjectedProps, ...OwnProps, ...StateProps |};

export default function ContactSupport({
  assetPath,
  backUrl,
  chatEnabled,
  currentUrl,
  isAuthenticatedUser,
  isNotForFqdn,
  isForIp,
  isCorporate,
  locale,
  location,
  logger,
  organizationsLoaded,
  personalDetails,
  phoneAdvisoryUrl,
  publicUrls,
  qbotApi,
  qbotAvailableLanguages,
  qbotUrl,
  questionId,
  reduxFormProps,
  routeParams,
  router,
  showPhishingWarn,
  showSupportWarn,
  sharingDetails,
  success,
  tmchUrl,
  translator,
  updating,
  userCountryCode,
  userInfos,
}: Props):
  | null
  | React.Element<typeof React.Fragment>
  | React.Element<typeof Layout> {
  const { __ } = translator;
  const dispatch: Dispatch = useDispatch();
  const lang = useSelector(localeSelector);
  // Corporate contact, Reseller contact, or "My rights" GRPD contact forms.
  const isStandalone = !!(
    routeParams.subject && routeParams.subject.match(/-/)
  );

  // Redirect to helpdesk before clean this component
  React.useEffect(() => {
    if (shouldRedirect(routeParams)) {
      const urlHelpDesk = urlRedirectToHelpdesk({
        lang,
        params: routeParams,
        query: location.query,
        isAuthenticated: isAuthenticatedUser,
      });
      window.location.href = urlHelpDesk;
    }

    if (routeParams.subject?.match(/\W$/) || routeParams.topic?.match(/\W$/)) {
      window.location.pathname = window.location.pathname.replace(/\W$/, '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (Object.keys(sharingDetails).length === 0) {
      dispatch(fetchCurrentOrganization());
    }
  }, [dispatch, sharingDetails]);

  React.useEffect(() => {
    if (Object.keys(personalDetails).length === 0) {
      dispatch(fetchPersonalOrganization());
    }
  }, [dispatch, personalDetails]);

  React.useEffect(() => {
    if (isAuthenticatedUser && !organizationsLoaded) {
      dispatch(fetchOrganizationsWithSubset('~name='));
    }
  }, [dispatch, isAuthenticatedUser, organizationsLoaded]);

  React.useEffect(() => {
    if (success && routeParams.subject) {
      router.push(`/success?${routeParams.subject}`);
    }
  }, [success, routeParams.subject, router]);

  const isLocaleAvailable = qbotAvailableLanguages.includes(locale);
  const hasParamUrlLiveChat = location.query && 'livechat' in location.query;

  const onSubmit = (values: IValues) => {
    const QBOT_IRREVELANT_ANSWER_RANK = 0;
    if (questionId !== '') {
      fetch(
        `${qbotApi}/analytics?question_id=${questionId}&rank=${QBOT_IRREVELANT_ANSWER_RANK}`,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
    }
    if (!updating) {
      const referrerUrl = backUrl || currentUrl;

      const { userAgent } = navigator;

      return dispatch(
        submitContactSupportForm(
          values,
          userAgent,
          referrerUrl,
          undefined,
          userCountryCode
        )
      ).catch(err => {
        if (err instanceof SubmissionError) {
          throw err;
        } else {
          dispatch(
            notifyDanger(
              __('A temporary error occured while sending your message.')
            )
          );
          logger.error(
            err,
            __('A temporary error occured while sending your message.')
          );
        }
      });
    }
    return Promise.resolve();
  };

  const renderContactSupportForm = () => {
    return (
      <ContactSupportForm
        isNotForFqdn={isNotForFqdn}
        isForIp={isForIp}
        isCorporate={isCorporate}
        isAuthenticatedUser={isAuthenticatedUser}
        isStandalone={isStandalone}
        onSubmit={onSubmit}
        personalDetails={personalDetails}
        reduxFormProps={reduxFormProps}
        routeParams={routeParams}
      />
    );
  };

  const renderFormIntro = () => {
    const isGRPDPage = routeParams.subject === 'my-rights';

    return (
      <Intro
        title={
          isGRPDPage
            ? __('Contact Gandi’s personal data protection service')
            : __('Ask help from the support team')
        }
      />
    );
  };

  const renderWarning = () => {
    // Corporate contact and Reseller contact forms.
    const isCorporateResellerContactForm = !!(
      routeParams.subject && routeParams.subject.match(/-contact$/)
    );

    if (isCorporateResellerContactForm) {
      return null;
    }

    const support = url.resolve(
      publicUrls.support,
      path.join(location.basename, location.pathname)
    );

    const urlLogin =
      typeof publicUrls.login === 'object'
        ? publicUrls.login.href
        : publicUrls.login;

    const login = url.resolve(
      publicUrls.support,
      `${urlLogin}?redirect=${encodeURIComponent(support)}`
    );

    const relogin = `${publicUrls.id}/logout?redirect=${encodeURIComponent(
      login
    )}`;
    const isGRPDPage = routeParams.subject === 'my-rights';

    let message: {| __html: string |};

    if (isAuthenticatedUser && personalDetails && personalDetails.name) {
      message = __(
        'You are logged as **%(username)s**. [Change](%(relogin)s)',
        {
          relogin,
          username: escapeMarkdown(personalDetails.name),
          withMarkdown: true,
          escapeParameters: false,
        }
      );
    } else if (!isAuthenticatedUser) {
      message = isGRPDPage
        ? __(
            'We can provide you with more personalized answers if you log into your account before using this form.',
            {
              withHTML: true,
            }
          )
        : __(
            'We can provide you with more personalized answers if you log into your account before using this form. ' +
              'If you are a reseller, you will need it to select your reseller organization.',
            {
              withHTML: true,
            }
          );
    } else {
      message = __('You are logged in.', { withHTML: true });
    }

    return (
      <Base
        padding="small"
        backgroundColor="grey-lighter"
        separatorTop
        className={[styles.marginBottom, styles.drawer].join(' ')}
      >
        <Base dangerouslySetInnerHTML={message} key="message" />

        {!isAuthenticatedUser ? (
          <Button
            href={
              typeof publicUrls.login === 'object'
                ? publicUrls.login.href
                : publicUrls.login
            }
            small
            kind="outline"
            className={styles.login}
          >
            {__('Log in')}
          </Button>
        ) : null}
      </Base>
    );
  };

  const getTitle = () => {
    if (routeParams.subject === 'my-rights') {
      return __('Exercise your rights to your personal data');
    }

    if (isCorporate) {
      return __('Contact Gandi Corporate Services');
    }

    return __('Contact Us');
  };

  const renderStandaloneIntro = () => {
    return (
      <Intro title={getTitle()}>
        <Base marginBottom="base">
          <Alert
            className={styles.alert}
            message={
              // eslint-disable-next-line no-nested-ternary
              routeParams.subject === 'reseller-contact'
                ? __(
                    'Send us a brief summary of your reseller needs.  \n\n' +
                      '' +
                      'For technical support and help about managing your ' +
                      'products, please use our ' +
                      '[Customer Support form](%(url)s).',
                    {
                      url: path.join(location.basename, 'contact'),
                      withMarkdown: true,
                    }
                  )
                : isCorporate
                ? __(
                    'Send us a brief summary of your corporate needs. ' +
                      'One of our Corporate Accounts managers will contact ' +
                      'you shortly.  \n\n' +
                      '' +
                      'For technical support and help about managing your ' +
                      'products, please use our ' +
                      '[Customer Support form](%(url)s).',
                    {
                      url: path.join(location.basename, 'contact'),
                      withMarkdown: true,
                    }
                  )
                : __(
                    '<p>' +
                      'Pursuant to Article 15 et seq. of the ' +
                      'General Data Protection Regulation (GDPR), ' +
                      'you have special rights to your personal data, ' +
                      'notably the right of rectification, erasure, ' +
                      'and restriction of data processed by Gandi.' +
                      '</p>\n\n<p>' +
                      'To exercise these rights, we ask that you fill out ' +
                      'the form below, which is for any individual ' +
                      'whose data may be processed by Gandi. ' +
                      'The mandatory fields are indicated by ' +
                      '“ <b style="color: #f00;">*</b> ”.' +
                      '</p>\n\n<p>' +
                      'Gandi will ask you to provide a valid proof of ID. ' +
                      '</p>\n\n<p>' +
                      'Gandi will process your request within one (1) ' +
                      'month of the reception of said request.' +
                      '</p>\n\n<p>' +
                      'Personal data sent within the framework ' +
                      'of the exercising of your rights is processed ' +
                      'with the exclusive purpose of meeting your demand ' +
                      'and is stored for a maximum duration of one (1) ' +
                      'year from the date of the request.' +
                      '</p>',
                    {
                      withHTML: true,
                    }
                  )
            }
          />
        </Base>
      </Intro>
    );
  };

  if (success) {
    return null;
  }

  if (isStandalone) {
    return (
      <>
        <Layout
          location={location}
          className={isCorporate ? styles.corporateLayout : null}
        >
          {renderStandaloneIntro()}
          {renderWarning()}
          {renderContactSupportForm()}
        </Layout>
        {(chatEnabled || hasParamUrlLiveChat) && (
          <Chat
            assetPath={assetPath}
            locale={locale}
            personalDetails={personalDetails}
            publicUrls={publicUrls}
            sharingDetails={sharingDetails}
            userInfos={userInfos}
            hasParamUrlLiveChat={hasParamUrlLiveChat}
          />
        )}
      </>
    );
  }

  return (
    <>
      <>
        <div className={styles.banner} />
        <GandiVector />
      </>

      <Layout location={location}>
        <HelpHeader paddingBottom={isLocaleAvailable ? 'small' : undefined}>
          <DocumentationSearch
            qbotUrl={qbotUrl}
            qbotApi={qbotApi}
            qbotAvailableLanguages={qbotAvailableLanguages}
            translator={translator}
            saveQuestionId={saveQuestionId}
            questionId={questionId}
          />
        </HelpHeader>

        <PhishingAlert
          translator={translator}
          showSupportWarn={showSupportWarn}
          showPhishingWarn={showPhishingWarn}
        />
        <Base itemScope itemType="https://schema.org/FAQPage">
          <Intro title={__('Frequently asked questions')}>
            <FAQCollapsible translator={translator} />
          </Intro>
          <DocumentationGuides translator={translator} />
          <div id="contact-support-form" className={styles.anchor}>
            {renderFormIntro()}
            {renderWarning()}
            {renderContactSupportForm()}

            <AdditionalServices
              phoneAdvisoryUrl={phoneAdvisoryUrl}
              tmchUrl={tmchUrl}
            />
          </div>
        </Base>
      </Layout>
      {(chatEnabled || hasParamUrlLiveChat) && (
        <Chat
          assetPath={assetPath}
          locale={locale}
          personalDetails={personalDetails}
          publicUrls={publicUrls}
          sharingDetails={sharingDetails}
          userInfos={userInfos}
          hasParamUrlLiveChat={hasParamUrlLiveChat}
        />
      )}
    </>
  );
}

ContactSupport.preFetch = function preFetch(
  args: Props & {
    dispatch: Dispatch,
    req: express$Request,
    ...
  }
): Promise<$TupleMap<Array<mixed>, <T>(p: Promise<T> | T) => T>> {
  const { dispatch, req } = args;

  const currentUrl = url.format({
    protocol: req.protocol,
    hostname: req.hostname,
    pathname: req.path,
  });

  const promises = [
    dispatch(
      setBackUrlParams({
        backUrl: req.headers.referer,
        currentUrl,
      })
    ),
    dispatch(fetchCurrentOrganization()),
    dispatch(fetchPersonalOrganization()),
  ];

  if (args.isAuthenticatedUser) {
    promises.push(dispatch(fetchOrganizationsWithSubset('~name=')));
  }

  if (req && req.body) {
    promises.push(
      dispatch(formActions.initialize('ContactSupportForm', req.body))
    );
  }

  return Promise.all(promises);
};

const selectHelpUrl = selectFromConfig<string>(
  'app.layout.urls.support',
  'https://help.gandi.net'
);

ContactSupport.handleRedirect = function handleRedirect(
  args: Props & {
    dispatch: Dispatch,
    getState: GetState,
    req: express$Request,
    res: express$Response,
  }
) {
  const { res, req } = args;
  const isAuthenticated = selectIsAuthenticatedUser(args.getState());

  if (shouldRedirect(args.params)) {
    const lang = localeSelector(args.getState());
    const urlHelpDesk = urlRedirectToHelpdesk({
      lang,
      params: args.params,
      // $FlowFixMe[incompatible-call]
      query: req.query,
      isAuthenticated,
    });
    res.redirect(urlHelpDesk);
  }

  if (args.params.subject?.match(/\W$/) || args.params.topic?.match(/\W$/)) {
    const pathname = args.location.pathname.replace(/\W$/, '');
    const helpUrl = selectHelpUrl(args.getState());
    const redirectUrl = url.resolve(
      helpUrl,
      path.join(args.location.basename, pathname)
    );
    res.redirect(redirectUrl);
  }
};
